export const userImage = (user) => {
    return user?.picture ?? null
}

/**
 * Claim normalisation across identity providers (Vue admin).
 *
 * Mirrors the React client's src/context/auth/claims.js. The two providers deliver the
 * same facts under different names:
 *
 *   Auth0     https://www.chaostrack.com/roles   /orgId
 *   Keycloak  ct_allowed_roles                   ct_org_id
 *
 * Auth0's names are URL-shaped and Keycloak treats "." in a claim name as a NESTING
 * separator, so emitting the Auth0 names from Keycloak would need escaping that is easy to
 * get subtly wrong. Normalising here keeps the difference in one place.
 *
 * This file previously read ONLY the Auth0 names. On Keycloak every reader returned
 * false/'' — which made Admin.vue's watcher leave every role flag false and then redirect
 * the user to the client app, so no manager could open the admin portal at all.
 *
 * Every reader accepts EITHER shape, so one build works against either provider.
 */
const AUTH0_NS = 'https://www.chaostrack.com'

const firstDefined = (...values) => values.find((v) => v !== undefined && v !== null)

/** Roles as an array, from either provider. */
export const claimRoles = (user) => {
    const value = firstDefined(user?.[`${AUTH0_NS}/roles`], user?.ct_allowed_roles)
    if (Array.isArray(value)) return value
    // Keycloak can emit a single-valued multivalued claim as a bare string.
    if (typeof value === 'string' && value.length > 0) return value.split(',').map((r) => r.trim()).filter(Boolean)
    return []
}

/** The org this subject belongs to, from either provider. */
export const claimOrgId = (user) => firstDefined(user?.[`${AUTH0_NS}/orgId`], user?.ct_org_id) ?? ''

export const isEmailUser = (user) => {
    // Auth0 encodes the passwordless plane in the subject prefix. Keycloak subjects are
    // bare UUIDs with no such signal, so the server stamps an authPlane attribute which
    // is surfaced as ct_auth_plane.
    if (user?.ct_auth_plane) return user.ct_auth_plane === 'otp'
    return user?.sub?.startsWith('email|') ?? false
}

export const userIdHeaderName = 'Auth-Access-Id'

export const authHeadersFor = (accessToken, userId) => {
    const authHeaders = { 'Authorization': `Bearer ${accessToken}` }
    authHeaders[userIdHeaderName] = userId // Provide this token for relaxed rate limiting for authenticated users
    return authHeaders
}

export const userIsRole = ({ user, role}) => {
    return claimRoles(user).includes(role)
}

export const userOrg = (user) => claimOrgId(user)

/**
 * From: https://stackoverflow.com/a/51655682/2333933
 * Returns a vue-router props function that maps the query params to component props according to the rules object passed in.
 * @param paramsToProps - Object defining param mapping rules: key = query name, value = prop name; Other params ignored.
 */
export const bindQueryParamsToProps = (paramsToProps) => {
    return (route) => {
        let paramsToPropsRules = paramsToProps ? Object.entries(paramsToProps) : []
        const paramProps = {}
        for (const [queryParam, propName] of paramsToPropsRules) {
            paramProps[propName] = route.query[queryParam]
        }
        return { ...route.params, ...paramProps }
    }
}

// Entitlements For Organizations, Users, etc.
export const entitlementTypes = Object.freeze({
    SCENARIO_BUILDER: 'SCENARIO_BUILDER', // Access to the scenario builder + customizer features
    VOICE_COPILOT: 'VOICE_COPILOT', // Access to the voice co-pilot feature
})

export const orgHasEntitlement = ({ org, entitlementType }) => {
    if (!org || !entitlementType) {
        return false
    }

    return org?.entitlements?.[entitlementType] === true ?? false
}